import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import {
  getSubscriptions,
  getSubscription,
  getSubscriptionInvoices,
  createSubscription,
  updateSubscription,
  deleteSubscription,
  updateSubscriptionStatus,
  getAccountsForSelect,
  getSalesUsers,
  getContractualManagerCandidates,
  getTechnicalManagerCandidates,
  assignInvoicesToSubscription,
  unassignInvoiceFromSubscription,
  searchInvoicesForSubscription,
  getSubscriptionQuotes,
  assignQuotesToSubscription,
  unassignQuoteFromSubscription,
  searchQuotesForSubscription,
  getSensorsBySubscription,
  addSensorToSubscription,
  removeSensorFromSubscription,
  getRenewalSummary,
  getReconciliation,
  getPortfolioTotals,
  getRevenueByMonth,
  addSubscriptionSensor,
  addAllSubscriptionSensors,
  removeSubscriptionSensor,
  getRenewalQuotePreview,
  createRenewalQuote,
  retagQuoteAsRecurring,
} from '../api';
import { subscriptionKeys } from './queryKeys';
import type {
  V4SubscriptionsFilters,
  CreateSubscriptionV4Input,
  UpdateSubscriptionV4Input,
  SubscriptionSensor,
} from '../types';

// ── Queries ────────────────────────────────────────────────────────────

export function useSubscriptions(filters: V4SubscriptionsFilters = {}) {
  return useQuery({
    queryKey: subscriptionKeys.list(filters),
    queryFn: () => getSubscriptions(filters),
  });
}

/** @deprecated Use useSubscriptions */
export const useContracts = useSubscriptions;

export function useSubscription(subscriptionId: string | null) {
  return useQuery({
    queryKey: subscriptionKeys.detail(subscriptionId!),
    queryFn: () => getSubscription(subscriptionId!).then(r => r.subscription),
    enabled: !!subscriptionId,
  });
}

/** @deprecated Use useSubscription */
export const useContract = useSubscription;

export function useSubscriptionInvoices(subscriptionId: string | null) {
  return useQuery({
    queryKey: subscriptionKeys.invoices(subscriptionId!),
    queryFn: () => getSubscriptionInvoices(subscriptionId!).then(r => r.invoices),
    enabled: !!subscriptionId,
  });
}

/** @deprecated Use useSubscriptionInvoices */
export const useContractInvoices = useSubscriptionInvoices;

export function useSubscriptionQuotes(subscriptionId: string | null) {
  return useQuery({
    queryKey: subscriptionKeys.quotes(subscriptionId!),
    queryFn: () => getSubscriptionQuotes(subscriptionId!).then(r => r.quotes),
    enabled: !!subscriptionId,
  });
}

export function useRenewalSummary(subscriptionId: string | null) {
  return useQuery({
    queryKey: subscriptionKeys.renewalSummary(subscriptionId!),
    queryFn: () => getRenewalSummary(subscriptionId!),
    enabled: !!subscriptionId,
  });
}

export function useReconciliation(subscriptionId: string | null) {
  return useQuery({
    queryKey: subscriptionKeys.reconciliation(subscriptionId!),
    queryFn: () => getReconciliation(subscriptionId!),
    enabled: !!subscriptionId,
  });
}

// Refresh the reconciliation lists AND the renewal summary (linked/unlinked counts)
// after a membership change, so the UI reflects the new junction state (S3).
function invalidateMembership(qc: ReturnType<typeof useQueryClient>, subscriptionId: string) {
  qc.invalidateQueries({ queryKey: subscriptionKeys.reconciliation(subscriptionId) });
  qc.invalidateQueries({ queryKey: subscriptionKeys.renewalSummary(subscriptionId) });
}

export function useAddSubscriptionSensor(subscriptionId: string | null) {
  const { t } = useTranslation('subscriptions');
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sensorId: string) => addSubscriptionSensor(subscriptionId!, sensorId),
    onSuccess: () => {
      invalidateMembership(queryClient, subscriptionId!);
      toast.success(t('reconciliation.addSuccess', 'Sensor linked to subscription'));
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(t('reconciliation.addError', 'Could not link the sensor: {{msg}}', { msg }));
    },
  });
}

export function useAddAllSubscriptionSensors(subscriptionId: string | null) {
  const { t } = useTranslation('subscriptions');
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sensorIds: string[]) => addAllSubscriptionSensors(subscriptionId!, sensorIds),
    onSuccess: (result) => {
      invalidateMembership(queryClient, subscriptionId!);
      const skipped = result.skippedSensorIds.length;
      toast.success(t('reconciliation.addAllSuccess', '{{added}} sensor(s) linked to the subscription', { added: result.added })
        + (skipped > 0
          ? t('reconciliation.addAllSkippedSuffix', ' — {{skipped}} skipped (not on this account or already linked)', { skipped })
          : ''));
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(t('reconciliation.addAllError', 'Could not link the sensors: {{msg}}', { msg }));
    },
  });
}

export function useRemoveSubscriptionSensor(subscriptionId: string | null) {
  const { t } = useTranslation('subscriptions');
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sensorId: string) => removeSubscriptionSensor(subscriptionId!, sensorId),
    onSuccess: () => {
      invalidateMembership(queryClient, subscriptionId!);
      toast.success(t('reconciliation.removeSuccess', 'Sensor unlinked from subscription'));
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(t('reconciliation.removeError', 'Could not unlink the sensor: {{msg}}', { msg }));
    },
  });
}


// Recurring-revenue portfolio totals across the whole book (S11).
export function usePortfolioTotals() {
  return useQuery({
    queryKey: subscriptionKeys.portfolioTotals(),
    queryFn: getPortfolioTotals,
  });
}

/** "Expected revenue by month" buckets (#1409), optionally scoped to one account manager. */
export function useRevenueByMonth(from: string, to: string, salesManagerId?: string, enabled = true) {
  return useQuery({
    queryKey: subscriptionKeys.revenueByMonth(from, to, salesManagerId),
    queryFn: () => getRevenueByMonth({ from, to, salesManagerId }),
    enabled,
  });
}

export function useRenewalQuotePreview(subscriptionId: string | null) {
  return useQuery({
    queryKey: subscriptionKeys.renewalQuotePreview(subscriptionId!),
    queryFn: () => getRenewalQuotePreview(subscriptionId!),
    enabled: !!subscriptionId,
  });
}

export function useCreateRenewalQuote() {
  const { t } = useTranslation('subscriptions');
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (subscriptionId: string) => createRenewalQuote(subscriptionId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
      const num = result?.quote?.quoteNumber;
      toast.success(t('renewalQuote.created', 'Renewal quote {{num}} created', { num }));
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(t('renewalQuote.createError', 'Could not create the renewal quote: {{msg}}', { msg }));
    },
  });
}

// Smart-picker companion: one-click "confirm the proposal" — tags the quote
// behind the subscription's newest invoice as recurring, then refetches so the
// renewal preview re-picks it (and the stale-reference warning clears).
export function useRetagQuoteAsRecurring() {
  const { t } = useTranslation('subscriptions');
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (quoteId: string) => retagQuoteAsRecurring(quoteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
      toast.success(t('renewalQuote.retagSuccess', 'Quote marked as recurring — the renewal proposal now uses it.'));
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(t('renewalQuote.retagError', 'Could not mark the quote as recurring: {{msg}}', { msg }));
    },
  });
}

export function useSearchQuotesForSubscription(search: string | undefined, accountId: string | undefined, page = 0) {
  return useQuery({
    queryKey: subscriptionKeys.quoteSearch(accountId!, search, page),
    queryFn: () => searchQuotesForSubscription({ search, accountId: accountId!, pageNumber: page, pageLength: 100 }),
    enabled: !!accountId,
  });
}

export function useAccountsForSelect(search?: string) {
  return useQuery({
    queryKey: subscriptionKeys.accounts(search),
    queryFn: () => getAccountsForSelect(search).then(r => r.accounts),
    staleTime: 5 * 60 * 1000,
  });
}

export function useSalesUsers() {
  return useQuery({
    queryKey: subscriptionKeys.salesUsers(),
    queryFn: () => getSalesUsers().then(r => r.users),
    staleTime: 5 * 60 * 1000,
  });
}

export function useContractualManagerCandidates(accountId: string | undefined) {
  return useQuery({
    queryKey: subscriptionKeys.contractualManagers(accountId!),
    queryFn: () => getContractualManagerCandidates(accountId!),
    enabled: !!accountId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useTechnicalManagerCandidates(accountId: string | undefined) {
  return useQuery({
    queryKey: subscriptionKeys.technicalManagers(accountId!),
    queryFn: () => getTechnicalManagerCandidates(accountId!),
    enabled: !!accountId,
    staleTime: 5 * 60 * 1000,
  });
}

// ── Mutations ──────────────────────────────────────────────────────────

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return 'An unexpected error occurred';
}

export function useCreateSubscription() {
  const { t } = useTranslation('subscriptions');
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateSubscriptionV4Input) => createSubscription(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.lists() });
      toast.success(t('toast.createSuccess'));
    },
    onError: (error) => {
      toast.error(t('toast.createError'), { description: getErrorMessage(error) });
    },
  });
}

/** @deprecated Use useCreateSubscription */
export const useCreateContract = useCreateSubscription;

export function useUpdateSubscription() {
  const { t } = useTranslation('subscriptions');
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSubscriptionV4Input }) => updateSubscription(id, data),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.detail(variables.id) });
      toast.success(t('toast.updateSuccess'));
    },
    onError: (error) => {
      toast.error(t('toast.updateError'), { description: getErrorMessage(error) });
    },
  });
}

/** @deprecated Use useUpdateSubscription */
export const useUpdateContract = useUpdateSubscription;

export function useDeleteSubscription() {
  const { t } = useTranslation('subscriptions');
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteSubscription(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.lists() });
      toast.success(t('toast.deleteSuccess'));
    },
    onError: (error) => {
      toast.error(t('toast.deleteError'), { description: getErrorMessage(error) });
    },
  });
}

/** @deprecated Use useDeleteSubscription */
export const useDeleteContract = useDeleteSubscription;

export function useUpdateSubscriptionStatus() {
  const { t } = useTranslation('subscriptions');
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => updateSubscriptionStatus(id, status),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.detail(variables.id) });
      toast.success(t('toast.updateSuccess'));
    },
    onError: (error) => {
      toast.error(t('toast.updateError'), { description: getErrorMessage(error) });
    },
  });
}

/** @deprecated Use useUpdateSubscriptionStatus */
export const useUpdateContractStatus = useUpdateSubscriptionStatus;

export function useSearchInvoicesForSubscription(search: string | undefined, accountId: string | undefined, page = 0) {
  return useQuery({
    queryKey: subscriptionKeys.invoiceSearch(accountId!, search, page),
    queryFn: () => searchInvoicesForSubscription({ search, accountId: accountId!, pageNumber: page, pageLength: 100 }),
    enabled: !!accountId,
  });
}

/** @deprecated Use useSearchInvoicesForSubscription */
export const useSearchInvoicesForContract = useSearchInvoicesForSubscription;

export function useAssignInvoicesToSubscription() {
  const { t } = useTranslation('subscriptions');
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ subscriptionId, invoiceIds }: { subscriptionId: string; invoiceIds: string[] }) =>
      assignInvoicesToSubscription(subscriptionId, invoiceIds),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.invoices(variables.subscriptionId) });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.lists() });
      if (result.linkedQuotes.length > 0) {
        toast.success(t('toast.assignSuccessWithQuotes', { quotes: result.linkedQuotes.join(', ') }));
      } else {
        toast.success(t('toast.assignSuccess'));
      }
    },
    onError: (error) => {
      toast.error(t('toast.assignError'), { description: getErrorMessage(error) });
    },
  });
}

/** @deprecated Use useAssignInvoicesToSubscription */
export const useAssignInvoicesToContract = useAssignInvoicesToSubscription;

export function useUnassignInvoiceFromSubscription() {
  const { t } = useTranslation('subscriptions');
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ subscriptionId, invoiceId }: { subscriptionId: string; invoiceId: string }) =>
      unassignInvoiceFromSubscription(subscriptionId, invoiceId),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.invoices(variables.subscriptionId) });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.lists() });
      if (result.unlinkedQuotes.length > 0) {
        toast.success(t('toast.unassignSuccessWithQuotes', { quotes: result.unlinkedQuotes.join(', ') }));
      } else {
        toast.success(t('toast.unassignSuccess'));
      }
    },
    onError: (error) => {
      toast.error(t('toast.unassignError'), { description: getErrorMessage(error) });
    },
  });
}

/** @deprecated Use useUnassignInvoiceFromSubscription */
export const useUnassignInvoiceFromContract = useUnassignInvoiceFromSubscription;

export function useAssignQuotesToSubscription() {
  const { t } = useTranslation('subscriptions');
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ subscriptionId, quoteIds }: { subscriptionId: string; quoteIds: string[] }) =>
      assignQuotesToSubscription(subscriptionId, quoteIds),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.quotes(variables.subscriptionId) });
      toast.success(t('quoteTable.assignSuccess'));
    },
    onError: (error) => {
      toast.error(t('quoteTable.assignError'), { description: getErrorMessage(error) });
    },
  });
}

export function useUnassignQuoteFromSubscription() {
  const { t } = useTranslation('subscriptions');
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ subscriptionId, quoteId }: { subscriptionId: string; quoteId: string }) =>
      unassignQuoteFromSubscription(subscriptionId, quoteId),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.quotes(variables.subscriptionId) });
      toast.success(t('quoteTable.unassignSuccess'));
    },
    onError: (error) => {
      toast.error(t('quoteTable.unassignError'), { description: getErrorMessage(error) });
    },
  });
}

// ── Subscription Sensors ─────────────────────────────────────────────

export function useSubscriptionSensors(subscriptionNumber: string | undefined) {
  return useQuery({
    queryKey: subscriptionKeys.sensors(subscriptionNumber!),
    queryFn: () => getSensorsBySubscription(subscriptionNumber!).then(r => r.sensors),
    enabled: !!subscriptionNumber,
    staleTime: 2 * 60 * 1000,
  });
}

export function useAddSensorToSubscription() {
  const { t } = useTranslation('subscriptions');
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sensorId, subscriptionNumber }: { sensorId: string; subscriptionNumber: string; sensor?: SubscriptionSensor }) =>
      addSensorToSubscription(sensorId, subscriptionNumber),
    onSuccess: (_result, variables) => {
      if (variables.sensor) {
        const key = subscriptionKeys.sensors(variables.subscriptionNumber);
        const existing = queryClient.getQueryData<SubscriptionSensor[]>(key) || [];
        if (!existing.some(s => s.id === variables.sensor!.id)) {
          queryClient.setQueryData<SubscriptionSensor[]>(key, [...existing, variables.sensor!]);
        }
      }
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.sensors(variables.subscriptionNumber) });
      toast.success(t('sensors.addSuccess'));
    },
    onError: (error) => {
      toast.error(t('sensors.addError'), { description: getErrorMessage(error) });
    },
  });
}

export function useRemoveSensorFromSubscription() {
  const { t } = useTranslation('subscriptions');
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sensorId, subscriptionNumber }: { sensorId: string; subscriptionNumber: string }) =>
      removeSensorFromSubscription(sensorId, subscriptionNumber),
    onSuccess: (_result, variables) => {
      const key = subscriptionKeys.sensors(variables.subscriptionNumber);
      const existing = queryClient.getQueryData<SubscriptionSensor[]>(key) || [];
      queryClient.setQueryData<SubscriptionSensor[]>(key, existing.filter(s => s.id !== variables.sensorId));
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.sensors(variables.subscriptionNumber) });
      toast.success(t('sensors.removeSuccess'));
    },
    onError: (error) => {
      toast.error(t('sensors.removeError'), { description: getErrorMessage(error) });
    },
  });
}
